import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { 
  Store,
  Gift,
  Percent,
  Star,
  MapPin,
  Phone,
  Globe,
  Mail,
  ChevronRight,
  Ticket,
  CheckCircle,
  Lock,
  Wrench,
  Users,
  ShoppingBag,
  Utensils,
  Heart,
  Music,
  Car,
  BookOpen,
  Search,
  Plus,
  Loader2,
  ExternalLink,
  Copy
} from "lucide-react";
import { LevelBadge } from "../components/LevelBadge";

const categoryIcons = {
  renovation: Wrench,
  services: Users,
  commerce: ShoppingBag,
  restauration: Utensils,
  sante: Heart,
  loisirs: Music,
  transport: Car,
  education: BookOpen
};

const PartnersPage = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [redemptionCode, setRedemptionCode] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [partnersRes, offersRes, categoriesRes, redemptionsRes] = await Promise.all([
        axios.get(`${API}/partners`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/offers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/partners/categories/list`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/user/redemptions`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setPartners(partnersRes.data);
      setOffers(offersRes.data);
      setCategories(categoriesRes.data.categories);
      setRedemptions(redemptionsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, fetchData]);

  const viewPartner = async (partnerId) => {
    try {
      const response = await axios.get(`${API}/partners/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPartner(response.data);
      setShowPartnerModal(true);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
  };

  const redeemOffer = async (offerId) => {
    try {
      const response = await axios.post(`${API}/offers/${offerId}/redeem`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRedemptionCode(response.data);
      setShowRedemptionModal(true);
      fetchData(); // Refresh redemptions
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'utilisation");
    }
  };

  const copyCode = () => {
    if (redemptionCode?.code) {
      navigator.clipboard.writeText(redemptionCode.code);
      toast.success("Code copié !");
    }
  };

  const filteredOffers = offers.filter(offer => {
    if (selectedCategory && offer.partner?.category !== selectedCategory) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        offer.title.toLowerCase().includes(query) ||
        offer.description.toLowerCase().includes(query) ||
        offer.partner_name?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const filteredPartners = partners.filter(partner => {
    if (selectedCategory && partner.category !== selectedCategory) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        partner.company_name.toLowerCase().includes(query) ||
        partner.description.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const getDiscountText = (offer) => {
    if (offer.discount_type === "percentage") {
      return `-${offer.discount_value}%`;
    } else if (offer.discount_type === "fixed") {
      return `-${offer.discount_value}€`;
    } else {
      return "GRATUIT";
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="animate-spin text-[#FF3366]" size={40} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 pb-20">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF3366]/10 text-[#FF3366] text-sm font-medium mb-3">
            <Store size={16} />
            Avantages
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Espace Partenaires
          </h1>
          <p className="text-slate-500 mt-1">Offres exclusives pour les membres SHARE</p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <Input
            placeholder="Rechercher une offre ou un partenaire..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-12 h-12 rounded-xl"
          />
        </div>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
          <Button
            variant={selectedCategory === null ? "default" : "outline"}
            className={`rounded-full whitespace-nowrap ${selectedCategory === null ? "bg-[#FF3366]" : ""}`}
            onClick={() => setSelectedCategory(null)}
          >
            Tous
          </Button>
          {categories.map(cat => {
            const Icon = categoryIcons[cat.id] || Store;
            return (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                className={`rounded-full whitespace-nowrap ${selectedCategory === cat.id ? "bg-[#FF3366]" : ""}`}
                onClick={() => setSelectedCategory(cat.id)}
              >
                <Icon size={16} className="mr-1" />
                {cat.name}
              </Button>
            );
          })}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="offers" className="w-full">
          <TabsList className="grid w-full grid-cols-3 rounded-xl h-12">
            <TabsTrigger value="offers" className="rounded-lg">
              <Gift size={16} className="mr-2" />
              Offres
            </TabsTrigger>
            <TabsTrigger value="partners" className="rounded-lg">
              <Store size={16} className="mr-2" />
              Partenaires
            </TabsTrigger>
            <TabsTrigger value="myoffers" className="rounded-lg">
              <Ticket size={16} className="mr-2" />
              Mes offres
            </TabsTrigger>
          </TabsList>

          {/* Offers Tab */}
          <TabsContent value="offers" className="space-y-4 mt-4">
            {filteredOffers.length === 0 ? (
              <Card className="guardian-card">
                <CardContent className="p-8 text-center text-slate-500">
                  Aucune offre disponible pour le moment
                </CardContent>
              </Card>
            ) : (
              filteredOffers.map(offer => (
                <Card key={offer.id} className="guardian-card overflow-hidden">
                  <div className="flex">
                    {/* Discount badge */}
                    <div className="w-24 bg-gradient-to-br from-[#FF3366] to-pink-500 flex flex-col items-center justify-center text-white p-4">
                      <span className="text-2xl font-extrabold">{getDiscountText(offer)}</span>
                      {offer.discount_type === "percentage" && <span className="text-xs opacity-80">réduction</span>}
                    </div>
                    
                    <CardContent className="flex-1 p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-bold text-slate-900">{offer.title}</h3>
                          <p className="text-sm text-[#FF3366]">{offer.partner_name}</p>
                          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{offer.description}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 mt-3">
                        {offer.min_rating > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <Star size={10} className="mr-1" />
                            {offer.min_rating}+ requis
                          </Badge>
                        )}
                        {offer.min_level !== "citoyen" && (
                          <Badge variant="outline" className="text-xs capitalize">
                            {offer.min_level}+
                          </Badge>
                        )}
                      </div>
                      
                      <div className="mt-3">
                        {offer.user_qualifies ? (
                          <Button 
                            className="w-full btn-guardian rounded-xl"
                            onClick={() => redeemOffer(offer.id)}
                          >
                            <Ticket size={16} className="mr-2" />
                            Utiliser cette offre
                          </Button>
                        ) : (
                          <Button 
                            variant="outline" 
                            className="w-full rounded-xl opacity-60"
                            disabled
                          >
                            <Lock size={16} className="mr-2" />
                            Conditions non remplies
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners" className="space-y-4 mt-4">
            {filteredPartners.length === 0 ? (
              <Card className="guardian-card">
                <CardContent className="p-8 text-center text-slate-500">
                  Aucun partenaire dans cette catégorie
                </CardContent>
              </Card>
            ) : (
              filteredPartners.map(partner => {
                const Icon = categoryIcons[partner.category] || Store;
                return (
                  <Card 
                    key={partner.id} 
                    className="guardian-card cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => viewPartner(partner.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center">
                          {partner.logo_url ? (
                            <img src={partner.logo_url} alt={partner.company_name} className="w-full h-full object-cover rounded-xl" />
                          ) : (
                            <Icon size={28} className="text-slate-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-slate-900">{partner.company_name}</h3>
                          <p className="text-sm text-slate-500 line-clamp-1">{partner.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs capitalize">
                              {partner.category}
                            </Badge>
                            <span className="text-xs text-slate-400">
                              {partner.total_offers} offre(s)
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="text-slate-300" size={20} />
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* My Offers Tab */}
          <TabsContent value="myoffers" className="space-y-4 mt-4">
            {redemptions.length === 0 ? (
              <Card className="guardian-card">
                <CardContent className="p-8 text-center">
                  <Ticket className="mx-auto text-slate-300 mb-4" size={48} />
                  <p className="text-slate-500">Vous n'avez pas encore utilisé d'offre</p>
                  <p className="text-sm text-slate-400 mt-1">Découvrez les offres disponibles !</p>
                </CardContent>
              </Card>
            ) : (
              redemptions.map(redemption => (
                <Card key={redemption.id} className="guardian-card">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-slate-900">{redemption.offer?.title}</h3>
                        <p className="text-sm text-[#FF3366]">{redemption.partner?.company_name}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          Utilisé le {new Date(redemption.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold text-[#FF3366]">{redemption.code}</p>
                        <Badge className={redemption.status === "active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}>
                          {redemption.status === "active" ? "Actif" : "Utilisé"}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>

        {/* Partner Modal */}
        <Dialog open={showPartnerModal} onOpenChange={setShowPartnerModal}>
          <DialogContent className="max-w-lg">
            {selectedPartner && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-xl">{selectedPartner.company_name}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-slate-600">{selectedPartner.description}</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="text-slate-400" />
                      <span>{selectedPartner.address}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-slate-400" />
                      <span>{selectedPartner.contact_phone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-slate-400" />
                      <span>{selectedPartner.contact_email}</span>
                    </div>
                    {selectedPartner.website && (
                      <a 
                        href={selectedPartner.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-[#FF3366]"
                      >
                        <Globe size={16} />
                        <span>Visiter le site</span>
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                  
                  {selectedPartner.offers?.length > 0 && (
                    <div className="pt-4 border-t">
                      <h4 className="font-bold mb-3">Offres disponibles</h4>
                      <div className="space-y-2">
                        {selectedPartner.offers.map(offer => (
                          <div key={offer.id} className="p-3 bg-slate-50 rounded-xl">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{offer.title}</p>
                                <p className="text-sm text-[#FF3366]">{getDiscountText(offer)}</p>
                              </div>
                              {offer.user_qualifies ? (
                                <Button 
                                  size="sm" 
                                  className="btn-guardian rounded-full"
                                  onClick={() => {
                                    setShowPartnerModal(false);
                                    redeemOffer(offer.id);
                                  }}
                                >
                                  Utiliser
                                </Button>
                              ) : (
                                <Lock size={16} className="text-slate-300" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Redemption Success Modal */}
        <Dialog open={showRedemptionModal} onOpenChange={setShowRedemptionModal}>
          <DialogContent className="max-w-sm text-center">
            <div className="py-4">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="text-green-600" size={40} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Offre activée !</h3>
              <p className="text-slate-500 mb-4">Présentez ce code chez le partenaire</p>
              
              {redemptionCode && (
                <div className="p-4 bg-slate-100 rounded-xl">
                  <p className="font-mono text-2xl font-bold text-[#FF3366] tracking-wider">
                    {redemptionCode.code}
                  </p>
                  <p className="text-sm text-slate-500 mt-2">{redemptionCode.offer_title}</p>
                </div>
              )}
              
              <Button 
                className="mt-4 w-full rounded-xl"
                variant="outline"
                onClick={copyCode}
              >
                <Copy size={16} className="mr-2" />
                Copier le code
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PartnersPage;
